import { useCallback } from 'react';

import { SHOWCASE_SUB_PAGE_BOARD_QUERY_PARAM } from '~/components/v2/components/showcase/showcaseSubPageBoardContext';
import { type PublicShowcaseContextValue } from '~/providers/PublicShowcaseProvider';

export type UsePublicUrlShowcaseBoardHrefProps = {
  /** The surface's route base — the live viewer's /a/{shortId} or the preview's own base. */
  basePath: string;
};

/**
 * The URL surfaces' board-entry href builder: a board route on the surface's own base, carrying the
 * listing page's slug as the `?page={slug}` breadcrumb trail (`null` = Home, no trail). Consumed
 * both by the surfaces supplying `PublicShowcaseLayout`'s `getBoardNavigationHref` (the live
 * `/a/…` page and the editor's Preview iframe) and internally by `usePublicUrlBoardNavigation`'s
 * drill pushes, so the href scheme has exactly one definition.
 */
export const usePublicUrlShowcaseBoardHref = ({ basePath }: UsePublicUrlShowcaseBoardHrefProps) => {
  const getBoardNavigationHref = useCallback<NonNullable<PublicShowcaseContextValue['getBoardNavigationHref']>>(
    ({ boardId, pageSlug }) =>
      pageSlug
        ? `${basePath}/b/${boardId}?${SHOWCASE_SUB_PAGE_BOARD_QUERY_PARAM}=${encodeURIComponent(pageSlug)}`
        : `${basePath}/b/${boardId}`,
    [basePath],
  );

  return { getBoardNavigationHref };
};
